import React from 'react';
import BlogPostTemplate from '../components/Blog/BlogPostTemplate';
import BlogMeta from '../components/Blog/BlogMeta';
import Callout from '../components/Blog/Callout';

type FlowStep = {
  title: string;
  detail: string;
};

type CaseStudyData = {
  title: string;
  subtitle: string;
  readTime: string;
  tags: string[];
  overview: string;
  problem: string;
  architecture: string[];
  dataModel: string[];
  technicalDetails: string[];
  decisions: string[];
  flow: FlowStep[];
  tradeoffs: string[];
  reliability: string[];
  next: string[];
};

const caseStudies: Record<string, CaseStudyData> = {
  demodock: {
    title: 'DemoDock Architecture',
    subtitle: 'A non-destructive visual sandbox for editing live web apps with natural language.',
    readTime: '9 min read',
    tags: ['Architecture', 'AI', 'Full-stack'],
    overview:
      'DemoDock was built to help teams tailor live product demos without waiting on engineering changes. The core idea was to keep the target application intact, place it inside a controlled preview surface, and translate natural-language requests into structured visual updates.',
    problem:
      'The challenge was balancing speed, safety, and compatibility. Live websites often block iframe rendering, active app state can be fragile, and natural-language edits need a predictable execution format before they touch the DOM.',
    architecture: [
      'The application is split into a chat/control surface, a preview surface, a proxy service, a credit/account layer, and a model API layer.',
      'The React UI owns user intent: target URL, chat history, credit state, authentication state, and the currently selected demo environment.',
      'The preview frame does not load arbitrary sites directly. It builds a proxied URL so target pages can be rendered inside a controlled DemoDock route.',
      'The Node proxy is responsible for fetching target HTML and assets, rewriting response headers, and injecting the bridge script needed for parent-to-frame communication.',
      'The model API returns structured responses to the frontend and updates the credit summary only after successful generation.',
    ],
    dataModel: [
      'Chat messages use a small role-based shape with generated message IDs, which keeps the UI append-only and easy to reason about.',
      'Credit state is represented as a summary object containing the active SKU, remaining credits, and plan metadata.',
      'Model output is treated as data rather than executable code: prompts are transformed into JSON actions before any page update is attempted.',
      'Demo targets are represented as URLs at the UI boundary and as proxied request paths at the preview boundary.',
    ],
    technicalDetails: [
      'The reverse proxy handles HTML, script, stylesheet, and asset requests so the preview can behave like a real browsing session instead of a static screenshot.',
      'The injected bridge listens for parent commands using postMessage and applies DOM-level updates in the child context, which avoids full page refreshes.',
      'The overlay approach keeps modifications non-destructive: DemoDock changes what the viewer sees, not the deployed application source.',
      'The AI path separates intent parsing from execution. A language model can propose edits, but the execution layer expects bounded action schemas.',
      'The tiered model setup uses a smaller fine-tuned model where cost matters and a larger LLM path where layout understanding or premium quality is needed.',
    ],
    decisions: [
      'Used a Next.js/Node.js stack so the interface, proxy, model calls, and demo state could be developed together.',
      'Built a reverse-proxy layer to render target websites inside a controlled sandbox while handling common iframe restrictions.',
      'Kept edits non-destructive by applying them through a browser overlay and JavaScript bridge instead of changing the source application.',
      'Converted prompts into structured JSON actions so model output could be validated before being executed.',
      'Separated lower-cost SLM analysis from higher-fidelity LLM workflows to manage quality and cost by tier.',
    ],
    flow: [
      { title: 'User prompt', detail: 'Plain-English request entered in the DemoDock chat.' },
      { title: 'AI pipeline', detail: 'Prompt becomes structured JSON actions for inspection and editing.' },
      { title: 'Proxy sandbox', detail: 'Target site is rendered through the controlled preview layer.' },
      { title: 'JS bridge', detail: 'Parent commands are passed into the page safely.' },
      { title: 'Visual update', detail: 'DOM changes are applied without mutating the source app.' },
    ],
    tradeoffs: [
      'A proxy adds operational complexity, but it gives the app enough control to support realistic live-site demos.',
      'DOM-level updates are faster than source-code generation, but they require careful handling of selectors and page state.',
      'Structured JSON reduces model creativity, but it makes the execution layer safer and easier to debug.',
    ],
    reliability: [
      'Credits are not deducted when model generation fails, so transient AI or network failures do not silently punish the user.',
      'The UI replaces the temporary “generating” assistant message with the final response, keeping chat state understandable after async work completes.',
      'The proxy boundary gives one place to handle cross-origin rendering issues rather than scattering compatibility fixes across the React app.',
      'The bridge keeps page mutation behind a narrow messaging contract, making future validation, logging, and replay easier to add.',
    ],
    next: [
      'Add stronger selector recovery for pages that re-render frequently.',
      'Persist reusable demo scripts so teams can replay the same personalized workflow.',
      'Expand automated masking rules for common sensitive data patterns.',
    ],
  },
  brainpocket: {
    title: 'BrainPocket Architecture',
    subtitle: 'A local-first knowledge tree for saving articles, key points, and reading state.',
    readTime: '8 min read',
    tags: ['Architecture', 'React', 'Local-first'],
    overview:
      'BrainPocket was designed as a small, private knowledge base that works without accounts or a traditional database. It stores article URLs, notes, read state, and domain groupings locally in the browser.',
    problem:
      'The product needed to feel useful immediately while keeping the data model portable. The main constraint was building enough structure for retrieval without introducing backend setup or user accounts.',
    architecture: [
      'BrainPocket is built around a custom React hook that owns persistence, selection state, search state, domain expansion state, imports, and derived totals.',
      'The page is split into a top bar, sidebar tree, and editor. This keeps global actions, navigation, and article editing independent.',
      'The storage utility is the persistence boundary: React code asks to load, save, export, and validate data rather than touching raw storage everywhere.',
      'An optional summarization API can fetch automated key points, but the app still works if the API is unavailable or times out.',
      'The UI reads from one normalized store, then derives filtered domain entries, selected article, unread counts, and key-point totals with memoized selectors.',
    ],
    dataModel: [
      'The root store is a domains object keyed by hostname, which maps each domain to an ordered list of saved articles.',
      'Each article stores an ID, title, normalized URL, saved date, read/unread state, and an array of key points.',
      'The selected article is stored by ID rather than by object reference, so updates can replace article objects without breaking selection.',
      'Import validation checks the full backup structure before replacing the current store, protecting the app from malformed JSON files.',
      'Key points are sanitized, trimmed, and capped when returned from the summarization service so automated output stays compact.',
    ],
    technicalDetails: [
      'Updates clone the existing store before modifying nested domain/article data, which keeps React state updates predictable.',
      'URL parsing normalizes user input into a clean URL, domain key, and display title before the article is inserted.',
      'Adding an existing URL replaces the previous article entry for that URL inside its domain group, avoiding duplicate saved links.',
      'Search is implemented across domain names, article titles, URLs, and key points, then returns only matching domain branches.',
      'The summarization request uses AbortController with a timeout so a slow backend cannot hang the article-capture flow.',
    ],
    decisions: [
      'Used React state with browser localStorage to make the app fast, private, and deployable as a simple client app.',
      'Designed the store around domains first, then articles, then key points, matching how users often remember sources.',
      'Added JSON import/export so local-first storage would not become a data lock-in problem.',
      'Kept article capture forgiving by parsing user-entered URLs and falling back to sensible titles.',
      'Made search traverse domains, titles, URLs, and key points so retrieval works across the whole tree.',
    ],
    flow: [
      { title: 'Save URL', detail: 'User adds an article link and optional title.' },
      { title: 'Parse domain', detail: 'URL is normalized and grouped by source domain.' },
      { title: 'Update store', detail: 'Article and key points are written to localStorage.' },
      { title: 'Search tree', detail: 'Domains and articles are filtered from local state.' },
      { title: 'Export backup', detail: 'The full knowledge base can be moved as JSON.' },
    ],
    tradeoffs: [
      'localStorage keeps the app simple and private, but it limits multi-device sync.',
      'A domain-first tree is easy to scan, but it is less flexible than a full tagging system.',
      'JSON backups are transparent, but they rely on the user to preserve copies when switching browsers.',
    ],
    reliability: [
      'Storage reads fall back to an empty store if no browser storage is available or the saved data cannot be parsed.',
      'The import path reports a clear validation error instead of partially merging unknown data into the current knowledge base.',
      'The summarization path fails soft by returning no automated key points, allowing the user to keep saving articles manually.',
      'Derived totals are recalculated from the store instead of being persisted separately, which avoids stale counters after import or deletion.',
    ],
    next: [
      'Add optional encrypted cloud sync while preserving local-first behavior.',
      'Support tags or collections for articles that belong to multiple themes.',
      'Add duplicate detection and richer metadata extraction.',
    ],
  },
  'wheel-of-chores': {
    title: 'Wheel of Chores Architecture',
    subtitle: 'A household chore planner with person-aware wheels, rewards, and mobile builds.',
    readTime: '8 min read',
    tags: ['Architecture', 'React', 'Capacitor'],
    overview:
      'Wheel of Chores turns shared household task planning into a small game. People have their own chore lists and Chore Coin balances, chores can be assigned to one or more people, and due tasks are packed into a daily time budget before being selected through an animated spinning wheel.',
    problem:
      'The app needed to make chores feel fair without overwhelming the household. That meant balancing randomness with practical constraints like assignees, frequency, available time, completion period, and who should receive credit for a shared task.',
    architecture: [
      'The web route and Capacitor mobile entrypoint share the same React component, with the mobile shell locking the experience into phone mode.',
      'Typed component state owns the active tab, selected person filter, rendering mode, people and chore forms, wheel rotation, selected chore, completion attribution, and persistent store.',
      "The persistent store contains people, assigned chores, rewards, completion history, max daily minutes, and the current day's generated wheel for the selected person or household view.",
      'The UI is divided into four workflows: wheel generation/spinning, chore management, people management, and reward redemption.',
      'All business rules are implemented as pure helper functions where possible: date keys, week keys, period keys, coin multipliers, ID creation, and wheel gradients.',
      'The visual wheel is CSS-driven with a conic-gradient generated from the number of available chores, keeping the animation lightweight.',
      'Capacitor build scripts select either Chloromance or Wheel of Chores, then configure native app IDs, names, URL schemes, splash images, and launcher icons for the chosen target.',
    ],
    dataModel: [
      'A person contains an ID, display name, and Chore Coin balance.',
      'A chore contains an ID, name, frequency, estimated minutes, and assignee IDs, allowing one chore to belong to one or many people.',
      'A completion records the original chore details, the person who completed it, awarded coins, completion timestamp, and period key so history remains meaningful even if the chore is later deleted.',
      'A reward contains an ID, name, and coin cost, which keeps the reward shop intentionally simple.',
      'The daily wheel stores the date, selected person filter, and selected chore IDs, so it can refresh when the calendar day or active person changes while still reflecting edited chore details.',
      'Completion eligibility is calculated from a set of choreId:periodKey pairs, not from mutable flags on the chore itself.',
    ],
    technicalDetails: [
      'Daily, weekly, and monthly period keys are derived from the current date so chores reappear only when their frequency window changes.',
      'The active person selector filters chores to Everyone or a specific person before availability and time-budget logic run.',
      'Wheel generation shuffles the currently available chores for that selected person or household and greedily adds chores while the running total fits under the daily minute budget.',
      'The spin selects a random index, calculates the segment size, rotates the wheel through multiple full turns, and resolves the selected chore after the animation delay.',
      'Coin value is calculated from minutes multiplied by a frequency multiplier, rewarding weekly and monthly chores more heavily than daily tasks.',
      "Completing a chore appends a bounded history entry, increments the completing person's coin balance, and removes the chore from today's wheel in one store update.",
      'Reward redemption spends coins from the active person, or the first household member when the Everyone view is selected.',
    ],
    decisions: [
      'Stored people, chores, rewards, history, and daily wheel state in one local store for predictable updates.',
      'Kept chores shared by assignee IDs rather than duplicating chore records per person, so completing a shared chore removes it for the whole household period.',
      'Used daily, weekly, and monthly period keys so completed chores disappear until they are due again.',
      "Generated each day's wheel from available chores for the selected person or household while respecting a maximum daily minute budget.",
      'Awarded Chore Coins based on estimated minutes and frequency multipliers to make harder chores feel worthwhile.',
      'Built separate tabs for the wheel, chore manager, people roster, and reward shop to keep the main workflow focused.',
      'Used target-specific mobile scripts so generic mobile commands remain Chloromance aliases while Wheel of Chores has explicit iOS and Android commands.',
    ],
    flow: [
      { title: 'Household roster', detail: 'People are added locally and each person keeps their own Chore Coin balance.' },
      { title: 'Assigned chore pool', detail: 'User-defined chores include frequency, estimated minutes, and one or more assignees.' },
      { title: 'Person filter', detail: 'The wheel can target Everyone or a selected person before due chores are calculated.' },
      { title: 'Availability filter', detail: 'Completed chores are removed for the current day, week, or month.' },
      { title: 'Time budget', detail: 'The selected wheel is filled without exceeding available minutes.' },
      { title: 'Spin', detail: 'Random selection picks the next task with animated feedback.' },
      { title: 'Reward loop', detail: 'Completion creates history and awards Chore Coins to the person who completed the chore.' },
    ],
    tradeoffs: [
      'Random assignment makes the app feel fair, but the time budget keeps it from becoming impractical.',
      'A local store avoids setup, but it means cross-device household sharing would need a future sync layer.',
      'Shared chores are completed once per period, which fits household accountability but does not yet model one copy per person.',
      'Estimated minutes are simple to enter, but they are only as accurate as the user makes them.',
    ],
    reliability: [
      'The load path migrates older localStorage data by adding default people and assigning legacy chores to the household.',
      'Corrupted localStorage falls back to default seed data instead of crashing the page.',
      "A date-change effect clears yesterday's wheel when the stored wheel date no longer matches today.",
      'If a person is deleted, their chores are reassigned to another household member so no chore becomes orphaned.',
      'Deleting a chore also removes its ID from the active wheel, preventing dangling references in the spin UI.',
      'Reward redemption checks the selected person balance before subtracting, so the shop cannot produce a negative balance.',
      'Focused tests cover wheel generation, tab workflows, corrupted storage fallback, adding people, and assigning person-specific chores.',
    ],
    next: [
      'Support chore difficulty and preferred days in addition to minutes.',
      'Add cloud sync so one household can share the same roster across devices.',
      'Add analytics for recurring chores that are skipped too often.',
    ],
  },
  'chloromance-tracker': {
    title: 'Chloromance Plant Tracker Architecture',
    subtitle: 'An account-backed plant care system with weather-aware hydration and resilient local state.',
    readTime: '10 min read',
    tags: ['Architecture', 'React', 'Supabase'],
    overview:
      'Chloromance Plant Tracker was built as a practical plant care dashboard for rooms, patios, windowsills, and propagation areas. It turns watering timestamps into live hydration status, weather-adjusted moisture estimates, WiltPower summaries, reminders, and signed-in cloud-backed plant records.',
    problem:
      'The main challenge was keeping plant care both personal and portable. The app needed fast local rendering, account-backed recovery, weather-aware urgency, optional reminders, and privacy-conscious auth flows without making simple watering actions feel heavy.',
    architecture: [
      'The application is a React page wrapped by a Supabase authentication gate, so plant data is scoped to the signed-in user before the tracker renders.',
      'The tracker owns typed local state for plants, current time, theme, boot splash, form state, selected location, reminders, weather location, weather snapshot, cloud sync messages, and success celebrations.',
      'Persistence uses a hybrid model: localStorage provides immediate user-scoped state, while Supabase stores cloud-backed plant records in a user_plants table.',
      'The web route and mobile build share the same tracker and auth gate, with REACT_APP_BUILD_TARGET selecting the Chloromance mobile shell when needed.',
      'Supabase auth handles email/password sign-in and sign-up, browser redirect callbacks, and Capacitor deep-link callbacks for native app flows.',
      'Hydration is recalculated from source data rather than stored as a mutable field, so every status reflects the latest timestamp, current clock value, and weather multiplier.',
      'Weather integration is isolated behind an Open-Meteo fetch function that validates response shape before returning a normalized weather snapshot.',
      'The UI is organized around an auth/session bar, hero actions, boot splash, WiltPower summary, weather panel, location toolbar, plant cards, add-plant drawer, privacy modal, and success celebration modal.',
    ],
    dataModel: [
      'A plant stores ID, name, variety, plant type, location, watering frequency in days, last-watered epoch timestamp, and an optional note.',
      'Cloud plant rows add user_id and updated_at at the Supabase boundary, then map back into the same frontend Plant shape.',
      'Hydration status is derived as elapsedRatio, moisture percentage, state, and next watering copy, rather than being persisted directly.',
      'Weather snapshots store temperature, weather code, human-readable condition, multiplier, reason, updatedAt timestamp, latitude, and longitude.',
      'Reminder state is stored as a plant-ID-to-hydration-state map so notifications fire on Thirsty or Critical transitions instead of every render.',
      'Theme, weather coordinates, reminder preference, notified states, and plant cache use user-scoped localStorage keys so multiple accounts do not overwrite each other on the same device.',
      'Companion planting rules are stored as a lookup table from plant type to companion and antagonist plant types.',
    ],
    technicalDetails: [
      'The hydration engine calculates frequencyMs from watering_frequency_days, compares it with elapsed time since last_watered_timestamp, then multiplies elapsed time by the weather multiplier.',
      'Moisture is clamped between 0 and 100, with state thresholds at 75 percent elapsed for Thirsty and more than 100 percent elapsed for Critical.',
      'Weather codes are classified into rain, clear, cloudy, fog, snow, showers, storms, or mixed conditions, then converted into a moisture-decay multiplier.',
      'Cool or rainy conditions slow moisture loss with a 0.8 multiplier, hot conditions increase moisture loss with a 1.25 multiplier, and normal conditions use baseline decay.',
      'A 10-second ticker updates now so hydration cards can move from Optimal to Thirsty or Critical without user interaction.',
      'Weather refreshes on initial load and every 30 minutes, with manual coordinate refresh and browser geolocation support.',
      'WiltPower is calculated as the average moisture percentage across all plants, giving the dashboard a single health score without storing another counter.',
      'Location summaries are derived with useMemo, including total plants, thirsty count, critical count, and per-location health counts.',
      'Batch watering maps every plant in a location to a shared timestamp, clears existing notification state for those plants, persists the changed plants, and applies a short visual confirmation animation.',
      'The cloud sync boot path prefers Supabase plants when present, otherwise uploads existing local plants for a newly signed-in user.',
      'A 20-plant access limit is enforced before opening the add-plant drawer or saving a new plant, with a paused Stripe-era upgrade scaffold left in comments for future subscriptions.',
      'Deleting a plant updates local state, removes the Supabase row by user ID and plant ID, clears notification state, and removes transient watered animation state.',
    ],
    decisions: [
      'Used epoch timestamps instead of stored due dates so hydration can be recalculated accurately when weather conditions change.',
      'Kept localStorage as the fast path while adding Supabase as the recovery/sync layer, giving the app quick startup and signed-in backup.',
      'Scoped all local keys by user ID so shared browsers can switch accounts without cross-contaminating plant records or preferences.',
      'Stored notified states separately from plants to avoid coupling notification behavior to the plant data model or cloud rows.',
      'Used Open-Meteo because it supports coordinate-based weather without requiring an API key.',
      'Added companion and antagonist badges at the card level so location planning advice appears beside the plant it affects.',
      'Kept Stripe subscription code commented rather than active while the product validates the simpler fixed-limit access model.',
    ],
    flow: [
      { title: 'Authenticate', detail: 'Supabase signs in the user and scopes plant state by user ID.' },
      { title: 'Load records', detail: 'Cloud plants hydrate the UI when available; local records act as a fallback and upload seed.' },
      { title: 'Plant profile', detail: 'User adds plant type, location, watering frequency, and notes.' },
      { title: 'Timestamp store', detail: 'Watering actions update last-watered epoch timestamps locally and in Supabase.' },
      { title: 'Weather modifier', detail: 'Open-Meteo conditions adjust the moisture decay multiplier.' },
      { title: 'Hydration engine', detail: 'Elapsed time and frequency produce moisture, state, and due text.' },
      { title: 'Care actions', detail: 'Cards, WiltPower, location summaries, reminders, and batch watering update from derived status.' },
    ],
    tradeoffs: [
      'Weather-adjusted decay is more realistic than fixed due dates, but it is still a heuristic rather than sensor-grade moisture measurement.',
      'The hybrid local/cloud model is more complex than localStorage alone, but it protects user data if browser storage is cleared.',
      'Browser notifications are convenient, but they depend on user permission and browser support.',
      'Companion planting rules are fast and transparent, but a static lookup table cannot capture every cultivar-specific edge case.',
      'The fixed plant limit keeps billing out of scope for now, but it means larger collections need a future plan system.',
    ],
    reliability: [
      'Plant reads validate stored records and normalize plant types before rendering, falling back to starter data if storage is missing or invalid.',
      'Supabase cloud load failures fall back to local state and show a cloud sync message instead of blocking the dashboard.',
      'Cloud saves are attempted after local state updates, so watering and plant entry stay responsive even during transient network errors.',
      'Weather loading validates latitude, longitude, HTTP success, temperature, and weather code before applying a multiplier.',
      'Weather fetching is skipped in the test environment and guarded for missing fetch support.',
      'Notification state resets when a plant returns to Optimal or is watered, preventing stale warning suppression.',
      'Geolocation failures and notification permission denials are handled with user-facing messages instead of silent errors.',
      'Auth redirect handling supports both hash tokens and authorization codes, then cleans browser URLs after web callbacks.',
    ],
    next: [
      'Support custom moisture rules per plant type, including humidity-sensitive plants.',
      'Add historical watering charts to show which zones are repeatedly reaching Critical.',
      'Reactivate the Stripe subscription scaffold when plan limits and checkout are ready.',
      'Add shared household roles for multi-person plant care.',
    ],
  },
  'flow-pet-rpg': {
    title: 'Flow Pet RPG Architecture',
    subtitle: 'A focus timer that turns sustained attention into pet progression, loot, and evolution.',
    readTime: '9 min read',
    tags: ['Architecture', 'TypeScript', 'Game Systems'],
    overview:
      'Flow Pet RPG combines a focus timer with lightweight game mechanics. Completed focus sessions grant experience, pet stages evolve by level, and milestone loot can be equipped as visual rewards.',
    problem:
      'The hard part was making focus time feel trustworthy. Browser timers can drift, users can leave the tab, and rewards need to feel earned rather than cosmetic noise.',
    architecture: [
      'Flow Pet RPG uses a small React wrapper only to mount and clean up a TypeScript DOM application.',
      'The app module owns game state, rendering, event listeners, persistence, focus detection, particle effects, inventory, and progression rules.',
      'A dedicated Web Worker owns timer ticking, pause, resume, reset, and completion messages so countdown logic is isolated from UI rendering.',
      'The worker sends typed events back to the main thread with remaining time, elapsed time, duration, mode, and timestamp.',
      'The main thread is the source of truth for rewards because it can combine worker ticks with document visibility and window focus state.',
    ],
    dataModel: [
      'App state stores timer status, selected session length, remaining time, session duration, credited focus time, level, XP, health, inventory, equipped items, opened milestones, pet stage, warning timestamp, and message.',
      'Pet stages are derived from level thresholds: egg, hatchling, juvenile, and final form.',
      'Costume items have IDs, names, equipment slots, descriptions, glyphs, and unlock levels.',
      'Equipment is modeled as a partial map from slot to item ID, which prevents multiple items occupying the same slot.',
      'Loot milestones are stored separately from items so progression can be tuned without changing inventory rendering.',
    ],
    technicalDetails: [
      'The worker computes remaining time from startedAt plus duration, rather than simply decrementing by interval ticks, reducing visible drift.',
      'Pausing records pausedAt and resume shifts startedAt forward by the pause duration, preserving the original remaining time.',
      'Credited focus time accumulates only when the timer is running, the document is visible, and the window has focus.',
      'Visibility and blur events pause an active focus quest and start a grace period; returning within the grace window resumes the worker.',
      'If the grace period expires, the app resets the worker, applies health and XP penalties, and marks the session as neglect.',
      'Level-up loops handle multiple level gains from a single XP award and open any newly eligible loot chests.',
    ],
    decisions: [
      'Used a Web Worker for countdown ticks so the timer stays responsive outside the main UI render loop.',
      'Persisted game state in localStorage so levels, inventory, health, and equipment survive refreshes.',
      'Tracked focus credit only while the page is visible and focused, then paused or penalized abandoned sessions.',
      'Modeled pet evolution as level thresholds so progression has clear milestones.',
      'Kept the app in a DOM-driven TypeScript module to support a custom game-like interface without a larger game engine.',
    ],
    flow: [
      { title: 'Begin quest', detail: 'User starts a 25 or 50 minute focus session.' },
      { title: 'Worker timer', detail: 'A Web Worker sends tick and completion events.' },
      { title: 'Focus guard', detail: 'Visibility and focus events pause or penalize unattended sessions.' },
      { title: 'Progression', detail: 'Credited minutes convert to EXP, levels, and evolution stages.' },
      { title: 'Loot', detail: 'Level milestones unlock equipment that can be equipped on the pet.' },
    ],
    tradeoffs: [
      'A Web Worker is more complex than setInterval in React, but it gives cleaner timer isolation.',
      'Strict focus validation makes rewards more meaningful, but it can feel unforgiving if users multitask.',
      'A custom DOM UI gives creative control, but it requires more manual rendering discipline than React components.',
    ],
    reliability: [
      'State loads defensively from localStorage, clamps numeric values, and falls back to a default state if parsing fails.',
      'Sessions that were running before a refresh are restored as paused rather than pretending the timer kept running honestly.',
      'Timer cleanup removes document and window listeners, resets the worker, clears particle timers, and empties the mount node.',
      'Buttons are disabled according to timer status so users cannot start overlapping focus, break, or rest sessions.',
      'Pet stage is derived during persistence and render, avoiding a mismatch between level and visual evolution.',
    ],
    next: [
      'Add a session history log so users can review focus patterns.',
      'Tune neglect rules with clearer warnings and recovery options.',
      'Move rendering into reusable components if the game grows beyond one dashboard.',
    ],
  },
};

type ProjectCaseStudyProps = {
  project: keyof typeof caseStudies;
};

function Flowchart({ steps }: { steps: FlowStep[] }) {
  return (
    <ol className="case-flowchart" aria-label="Architecture flow">
      {steps.map((step) => (
        <li key={step.title}>
          <strong>{step.title}</strong>
          <span>{step.detail}</span>
        </li>
      ))}
    </ol>
  );
}

function ProjectCaseStudy({ project }: ProjectCaseStudyProps) {
  const study = caseStudies[project];

  return (
    <section className="page">
      <BlogPostTemplate
        genre="Case Study"
        title={study.title}
        subtitle={study.subtitle}
      >
        <BlogMeta
          author="Prapti Setty"
          publishedAt="Draft"
          readTime={study.readTime}
          tags={study.tags}
        />

        <p>{study.overview}</p>

        <Callout tone="insight" title="Engineering focus">
          <p>{study.problem}</p>
        </Callout>

        <h2>Architecture Flow</h2>
        <Flowchart steps={study.flow} />

        <h2>System Architecture</h2>
        <ul>
          {study.architecture.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>

        <h2>Data Model</h2>
        <ul>
          {study.dataModel.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>

        <h2>Implementation Details</h2>
        <ul>
          {study.technicalDetails.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>

        <h2>Key Decisions</h2>
        <ul>
          {study.decisions.map((decision) => (
            <li key={decision}>{decision}</li>
          ))}
        </ul>

        <h2>Tradeoffs</h2>
        <ul>
          {study.tradeoffs.map((tradeoff) => (
            <li key={tradeoff}>{tradeoff}</li>
          ))}
        </ul>

        <h2>Reliability Notes</h2>
        <ul>
          {study.reliability.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>

        <h2>What I Would Improve Next</h2>
        <ul>
          {study.next.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </BlogPostTemplate>
    </section>
  );
}

export default ProjectCaseStudy;
